import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";
import { ChatListItem } from "@/components/UI/tools";
import { useNavigate } from "react-router-dom";
import { runsState } from "@/app/runsState";
import { draftsState } from "@/app/draftsState";
import { RunBadge, runIndicator } from "@/components/navbar/runningChats";

/** State of the tag's chat run, as the chat's tool-status dot; nothing when there is none to show. */
const tagRunBadge = (tagId: number) => {
    const run = runsState.forTag(tagId);
    return runIndicator(run) ? <RunBadge run={run} /> : undefined;
};

/** Number of drafts left in the chats that include this tag; nothing when there are none. */
export const DraftBadge = observer(({ tagId }: { tagId: number }) => {
    const count = draftsState.countForTag(tagId);
    if (count === 0) return null;
    return (
        <span className="nav-item-corner-badge" data-testid={`draft-badge-${tagId}`} title={appState.t("chat.draftsIndicator", { count })}>
            {count}
        </span>
    );
});

export const ChatTagList = observer(({ tagIds }: { tagIds: number[] }) => {
    const navigate = useNavigate();
    const organisationId = appState.user?.active_organisation?.id;

    useEffect(() => {
        draftsState.clear();
        void draftsState.load();
    }, [organisationId]);

    const setTagIds = (action: number[] | ((prev: number[]) => number[])) => {
        const newIds = typeof action === 'function' ? action(tagIds) : action;
        if (newIds.length === 0) {
            navigate("/workspace/chat");
        } else {
            navigate(`/workspace/chat-${newIds.sort((a, b) => a - b).join("-")}`);
        }
    };

    const clients = appState.tags.filter(x => x.effective_permission_type > 1 && x.attributes?.is_client);
    const other = appState.tags.filter(x => x.effective_permission_type > 1 && !x.attributes?.is_client);

    return (
        <div className="flex flex-col h-full  w-full p-1 scrollbar-left my-1 overflow-x-hidden select-none z-20">
            <div className="timeline-rail timeline-rail-right my-1">
                <span className="sidebar-caption">clients</span>
            </div>
            <div className="flex flex-col items-start gap-1s">
                {clients.map(x => (
                    <ChatListItem
                        key={x.id}
                        leading={tagRunBadge(x.id)}
                        corner={<DraftBadge tagId={x.id} />}
                        onClick={(e) => {
                            tagIds.includes(x.id)
                                ? setTagIds(p => p.filter(y => y !== x.id))
                                : e.ctrlKey
                                    ? setTagIds(p => [...p, x.id])
                                    : setTagIds([x.id]);
                        }}
                        isSelected={tagIds.includes(x.id)}
                        isDisabled={false}
                    >
                        {x.name}
                    </ChatListItem>
                ))}
            </div>
            <div className="timeline-rail timeline-rail-right my-1 gap-1">
                <span className="sidebar-caption">other</span>
            </div>
            <div className="flex flex-col items-start gap-1s">
                {other.map(x => (
                    <ChatListItem
                        key={x.id}
                        leading={tagRunBadge(x.id)}
                        corner={<DraftBadge tagId={x.id} />}
                        onClick={(e) => {
                            tagIds.includes(x.id)
                                ? setTagIds(p => p.filter(y => y !== x.id))
                                : e.ctrlKey
                                    ? setTagIds(p => [...p, x.id])
                                    : setTagIds([x.id]);
                        }}
                        isSelected={tagIds.includes(x.id)}
                        isDisabled={false}
                    >
                        {x.name}
                    </ChatListItem>
                ))}
            </div>
        </div>
    );
});
